import { useState } from "react";
import { Link, useParams } from "react-router";
import { ANREDEN, berlinDatumLang, type Anrede } from "@dj/domain/gemeinsam";
import { Ansicht } from "../design/Ansicht";
import { Dialog } from "../design/Dialog";
import { Auswahl, Textfeld } from "../design/Formular";

const FILTER = ["alle", "ohne Passkey", "Einladung aktiv", "nur Link-Zugang"] as const;

function passt(z: Zeile, f: string): boolean {
  const zugang = String(z["Zugang"] ?? "");
  if (f === "ohne Passkey") return !zugang.startsWith("Passkey");
  if (f === "Einladung aktiv") return z["Einladung"] === "aktiv";
  if (f === "nur Link-Zugang") return zugang === "nur Link-Zugang";
  return true;
}
import { Abweisung } from "../design/Hinweis";
import { Knopf } from "../design/Kachel";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useSammlung, type Zeile } from "../plattform/lesemodelle";
import { useReviername } from "./Revier";

/**
 * Teilnehmerübersicht des Reviers (oberflaeche.md §7, lesemodelle.md §2): `region` „Teilnehmer des Reviers“ mit
 * `heading` „Teilnehmende“, je Teilnehmer `link` „<Name>“; „Hinzufügen“ steht fest unter der Liste, nie in ihr (N11), und
 * öffnet den Dialog „Teilnehmer“.
 */
export function zugangsstatus(z: Zeile): { symbol: string; text: string; voll: boolean } {
  const zugang = String(z["Zugang"] ?? "");
  if (zugang.startsWith("Passkey")) return { symbol: "passkey", text: zugang, voll: true };
  if (zugang === "nur Link-Zugang") return { symbol: "link", text: zugang, voll: false };
  if (z["Einladung"] === "aktiv") {
    const bis = z["gültig bis"] instanceof Date ? ` bis ${berlinDatumLang(z["gültig bis"] as Date)}` : "";
    return { symbol: "einladung", text: `Einladung aktiv${bis}`, voll: false };
  }
  return { symbol: "teilnehmer", text: zugang || "nicht verknüpft", voll: false };
}

export function Teilnehmeruebersicht() {
  const { revierId = "" } = useParams();
  const name = useReviername(revierId);
  const liste = useSammlung(`reviere/${revierId}/teilnehmeruebersicht`);
  const [offen, setOffen] = useState(false);
  const [filter, setFilter] = useState<string>("alle");
  const zeilen = [...liste.daten].filter((z) => passt(z, filter)).sort((a, b) => String((a["Teilnehmer"] as { Name?: string })?.Name).localeCompare(String((b["Teilnehmer"] as { Name?: string })?.Name), "de"));
  return (
    <Ansicht name="Teilnehmerübersicht" symbol="teilnehmer" unterzeile={name ?? undefined}
      unten={<Knopf art="planung" symbol="einladung" onPress={() => setOffen(true)}>Hinzufügen</Knopf>}>
      <Auswahl label="Filter" optionen={FILTER} wert={filter} onChange={setFilter} />
      <section className="liste-bereich platte" role="region" aria-label="Teilnehmer des Reviers">
        <h2 className="kopf"><Symbol name="teilnehmer" />Teilnehmende</h2>
        {zeilen.map((z) => {
          const t = z["Teilnehmer"] as { Name: string; Anrede: Anrede };
          const s = zugangsstatus(z);
          return (
            <Link key={z._id} to={`/reviere/${revierId}/teilnehmer/${z._id}`} className="zeile zeile--knopf" aria-label={t.Name}>
              <Symbol name={z["Rolle im Revier"] === "Jagdherr" ? "jagdherr" : "teilnehmer"} />
              <div>
                <div className="zeile__t">{t.Name}</div>{" "}
                <div className="zeile__u">{t.Anrede}{z["Rolle im Revier"] ? ` · ${String(z["Rolle im Revier"])}` : ""}</div>
                <div className="zeile__marken"><span className={s.voll ? "marke-status marke-status--voll" : "marke-status"}><Symbol name={s.symbol} />{s.text}</span></div>
              </div>
              <Symbol name="weiter" className="zeile__pfeil" />
            </Link>
          );
        })}
        {zeilen.length === 0 && !liste.laedt ? <div className="leer"><Symbol name="teilnehmer" /><span>Noch keine Teilnehmer</span></div> : null}
      </section>
      <TeilnehmerDialog revierId={revierId} offen={offen} onSchliessen={() => setOffen(false)} />
    </Ansicht>
  );
}

function TeilnehmerDialog({ revierId, offen, onSchliessen }: { revierId: string; offen: boolean; onSchliessen: () => void }) {
  const [name, setName] = useState("");
  const [anrede, setAnrede] = useState<string | null>(null);
  const [telefon, setTelefon] = useState("");
  const [grund, setGrund] = useState<string | null>(null);
  async function speichern() {
    setGrund(null);
    const daten: Record<string, unknown> = { Revier: revierId };
    if (name) daten["Name"] = name;
    if (anrede) daten["Anrede"] = anrede;
    if (telefon) daten["Telefonnummer"] = telefon;
    const a = await befehl("TeilnehmerAnlegen", daten);
    if (!a.ok) {
      setGrund(abweisungsgrund(a));
      return;
    }
    setName(""); setAnrede(null); setTelefon("");
    onSchliessen();
  }
  return (
    <Dialog name="Teilnehmer" symbol="teilnehmer" offen={offen} onSchliessen={onSchliessen}>
      <div className="formular">
        <Textfeld label="Name" wert={name} onChange={setName} />
        <Auswahl label="Anrede" optionen={ANREDEN} wert={anrede} onChange={setAnrede} />
        <Textfeld label="Telefonnummer" wert={telefon} onChange={setTelefon} typ="tel" />
        <Abweisung grund={grund} />
        <div className="knopfreihe">
          <Knopf onPress={speichern}>Speichern</Knopf>
          <Knopf art="zweit" onPress={onSchliessen}>Abbrechen</Knopf>
        </div>
      </div>
    </Dialog>
  );
}
